import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Display-density preference. ``comfortable`` is the default; ``compact``
 * tightens row padding + spacing for power users staring at long run
 * lists / DAGs.
 *
 * Surfaces consume the preference two ways:
 *   - CSS: ``[data-density="compact"]`` on ``<html>`` is set by
 *     :func:`applyDensityAttr`; tailwind.config.js reads it via the
 *     ``[data-density="compact"]`` selector to swap row gutters in
 *     the AG-Grid theme + general layout.
 *   - Direct read: components that don't have a CSS hook (e.g. a
 *     reactflow MiniMap height) read the store and pass props.
 */
export type Density = "comfortable" | "compact";

interface DensityState {
  density: Density;
  setDensity: (density: Density) => void;
}

export const useDensityStore = create<DensityState>()(
  persist(
    (set) => ({
      density: "comfortable",
      setDensity: (density) => set({ density }),
    }),
    { name: "flowstack-density" }
  )
);

/** Project the preference to the root ``<html>`` element so the CSS
 * selectors registered in tailwind.config.js + ag-grid-theme.css pick
 * it up immediately. Called once at app boot + on every change. */
export function applyDensityAttr(density: Density): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-density", density);
}
